/**
 * Industry configuration data for the onboarding wizard.
 * Contains 12 industry clusters with 101 total industries.
 *
 * This is static configuration data that changes infrequently,
 * so it's stored as a typed module rather than in the database.
 */

export interface IndustryCluster {
  id: string;
  name: string;
  industries: Industry[];
}

export interface Industry {
  id: string;
  name: string;
  clusterId: string;
}

export const INDUSTRY_CLUSTERS: IndustryCluster[] = [
  {
    id: 'manufacturing-and-industrial',
    name: 'Manufacturing & Industrial',
    industries: [
      { id: 'textile-manufacturing', name: 'Textile Manufacturing', clusterId: 'manufacturing-and-industrial' },
      { id: 'food-processing', name: 'Food Processing', clusterId: 'manufacturing-and-industrial' },
      { id: 'chemical-manufacturing', name: 'Chemical Manufacturing', clusterId: 'manufacturing-and-industrial' },
      { id: 'pharmaceutical-manufacturing', name: 'Pharmaceutical Manufacturing', clusterId: 'manufacturing-and-industrial' },
      { id: 'automobile-manufacturing', name: 'Automobile Manufacturing', clusterId: 'manufacturing-and-industrial' },
      { id: 'electronics-manufacturing', name: 'Electronics Manufacturing', clusterId: 'manufacturing-and-industrial' },
      { id: 'steel-and-metals', name: 'Steel & Metals', clusterId: 'manufacturing-and-industrial' },
      { id: 'plastics-and-rubber', name: 'Plastics & Rubber', clusterId: 'manufacturing-and-industrial' },
      { id: 'paper-and-packaging', name: 'Paper & Packaging', clusterId: 'manufacturing-and-industrial' },
      { id: 'cement-and-ceramics', name: 'Cement & Ceramics', clusterId: 'manufacturing-and-industrial' },
    ],
  },
  {
    id: 'services-and-professionals',
    name: 'Services & Professionals',
    industries: [
      { id: 'legal-services', name: 'Legal Services', clusterId: 'services-and-professionals' },
      { id: 'accounting-and-auditing', name: 'Accounting & Auditing', clusterId: 'services-and-professionals' },
      { id: 'consulting-services', name: 'Consulting Services', clusterId: 'services-and-professionals' },
      { id: 'healthcare-services', name: 'Healthcare Services', clusterId: 'services-and-professionals' },
      { id: 'education-and-training', name: 'Education & Training', clusterId: 'services-and-professionals' },
      { id: 'financial-services', name: 'Financial Services', clusterId: 'services-and-professionals' },
      { id: 'insurance-services', name: 'Insurance Services', clusterId: 'services-and-professionals' },
      { id: 'real-estate-services', name: 'Real Estate Services', clusterId: 'services-and-professionals' },
      { id: 'staffing-and-recruitment', name: 'Staffing & Recruitment', clusterId: 'services-and-professionals' },
    ],
  },
  {
    id: 'trade-retail-and-distribution',
    name: 'Trade, Retail & Distribution',
    industries: [
      { id: 'wholesale-trading', name: 'Wholesale Trading', clusterId: 'trade-retail-and-distribution' },
      { id: 'retail-general', name: 'Retail - General', clusterId: 'trade-retail-and-distribution' },
      { id: 'fmcg-distribution', name: 'FMCG Distribution', clusterId: 'trade-retail-and-distribution' },
      { id: 'electronics-retail', name: 'Electronics Retail', clusterId: 'trade-retail-and-distribution' },
      { id: 'fashion-and-apparel', name: 'Fashion & Apparel', clusterId: 'trade-retail-and-distribution' },
      { id: 'grocery-and-supermarket', name: 'Grocery & Supermarket', clusterId: 'trade-retail-and-distribution' },
      { id: 'automobile-dealership', name: 'Automobile Dealership', clusterId: 'trade-retail-and-distribution' },
      { id: 'building-materials-trading', name: 'Building Materials Trading', clusterId: 'trade-retail-and-distribution' },
      { id: 'ecommerce-retail', name: 'E-Commerce Retail', clusterId: 'trade-retail-and-distribution' },
    ],
  },
  {
    id: 'agriculture-and-rural-enterprises',
    name: 'Agriculture & Rural Enterprises',
    industries: [
      { id: 'crop-farming', name: 'Crop Farming', clusterId: 'agriculture-and-rural-enterprises' },
      { id: 'dairy-farming', name: 'Dairy Farming', clusterId: 'agriculture-and-rural-enterprises' },
      { id: 'poultry-and-livestock', name: 'Poultry & Livestock', clusterId: 'agriculture-and-rural-enterprises' },
      { id: 'fisheries-and-aquaculture', name: 'Fisheries & Aquaculture', clusterId: 'agriculture-and-rural-enterprises' },
      { id: 'agri-processing', name: 'Agri Processing', clusterId: 'agriculture-and-rural-enterprises' },
      { id: 'organic-farming', name: 'Organic Farming', clusterId: 'agriculture-and-rural-enterprises' },
      { id: 'floriculture-and-horticulture', name: 'Floriculture & Horticulture', clusterId: 'agriculture-and-rural-enterprises' },
      { id: 'seed-and-fertilizer', name: 'Seed & Fertilizer', clusterId: 'agriculture-and-rural-enterprises' },
    ],
  },
  {
    id: 'infrastructure-and-construction',
    name: 'Infrastructure & Construction',
    industries: [
      { id: 'civil-construction', name: 'Civil Construction', clusterId: 'infrastructure-and-construction' },
      { id: 'residential-construction', name: 'Residential Construction', clusterId: 'infrastructure-and-construction' },
      { id: 'commercial-construction', name: 'Commercial Construction', clusterId: 'infrastructure-and-construction' },
      { id: 'road-and-highway', name: 'Road & Highway', clusterId: 'infrastructure-and-construction' },
      { id: 'electrical-infrastructure', name: 'Electrical Infrastructure', clusterId: 'infrastructure-and-construction' },
      { id: 'plumbing-and-sanitation', name: 'Plumbing & Sanitation', clusterId: 'infrastructure-and-construction' },
      { id: 'interior-design-and-fitout', name: 'Interior Design & Fitout', clusterId: 'infrastructure-and-construction' },
      { id: 'heavy-machinery-and-equipment', name: 'Heavy Machinery & Equipment', clusterId: 'infrastructure-and-construction' },
    ],
  },
  {
    id: 'industrial-services-and-support',
    name: 'Industrial Services & Support',
    industries: [
      { id: 'facility-management', name: 'Facility Management', clusterId: 'industrial-services-and-support' },
      { id: 'security-services', name: 'Security Services', clusterId: 'industrial-services-and-support' },
      { id: 'cleaning-and-sanitation', name: 'Cleaning & Sanitation', clusterId: 'industrial-services-and-support' },
      { id: 'industrial-maintenance', name: 'Industrial Maintenance', clusterId: 'industrial-services-and-support' },
      { id: 'testing-and-calibration', name: 'Testing & Calibration', clusterId: 'industrial-services-and-support' },
      { id: 'waste-management', name: 'Waste Management', clusterId: 'industrial-services-and-support' },
      { id: 'manpower-supply', name: 'Manpower Supply', clusterId: 'industrial-services-and-support' },
      { id: 'printing-and-publishing', name: 'Printing & Publishing', clusterId: 'industrial-services-and-support' },
    ],
  },
  {
    id: 'innovation-technology-and-emerging-startups',
    name: 'Innovation, Technology & Emerging Startups',
    industries: [
      { id: 'software-development', name: 'Software Development', clusterId: 'innovation-technology-and-emerging-startups' },
      { id: 'it-consulting', name: 'IT Consulting', clusterId: 'innovation-technology-and-emerging-startups' },
      { id: 'fintech', name: 'Fintech', clusterId: 'innovation-technology-and-emerging-startups' },
      { id: 'healthtech', name: 'Healthtech', clusterId: 'innovation-technology-and-emerging-startups' },
      { id: 'edtech', name: 'Edtech', clusterId: 'innovation-technology-and-emerging-startups' },
      { id: 'agritech', name: 'Agritech', clusterId: 'innovation-technology-and-emerging-startups' },
      { id: 'artificial-intelligence-and-ml', name: 'Artificial Intelligence & ML', clusterId: 'innovation-technology-and-emerging-startups' },
      { id: 'cybersecurity', name: 'Cybersecurity', clusterId: 'innovation-technology-and-emerging-startups' },
      { id: 'saas-and-cloud', name: 'SaaS & Cloud', clusterId: 'innovation-technology-and-emerging-startups' },
    ],
  },
  {
    id: 'green-environment-and-utilities',
    name: 'Green, Environment & Utilities',
    industries: [
      { id: 'solar-energy', name: 'Solar Energy', clusterId: 'green-environment-and-utilities' },
      { id: 'wind-energy', name: 'Wind Energy', clusterId: 'green-environment-and-utilities' },
      { id: 'water-treatment', name: 'Water Treatment', clusterId: 'green-environment-and-utilities' },
      { id: 'recycling-and-upcycling', name: 'Recycling & Upcycling', clusterId: 'green-environment-and-utilities' },
      { id: 'electric-vehicles', name: 'Electric Vehicles', clusterId: 'green-environment-and-utilities' },
      { id: 'environmental-consulting', name: 'Environmental Consulting', clusterId: 'green-environment-and-utilities' },
      { id: 'biogas-and-biomass', name: 'Biogas & Biomass', clusterId: 'green-environment-and-utilities' },
      { id: 'carbon-credits-and-trading', name: 'Carbon Credits & Trading', clusterId: 'green-environment-and-utilities' },
    ],
  },
  {
    id: 'travel-tourism-and-hospitality',
    name: 'Travel, Tourism & Hospitality',
    industries: [
      { id: 'hotels-and-resorts', name: 'Hotels & Resorts', clusterId: 'travel-tourism-and-hospitality' },
      { id: 'restaurants-and-catering', name: 'Restaurants & Catering', clusterId: 'travel-tourism-and-hospitality' },
      { id: 'travel-agency', name: 'Travel Agency', clusterId: 'travel-tourism-and-hospitality' },
      { id: 'adventure-tourism', name: 'Adventure Tourism', clusterId: 'travel-tourism-and-hospitality' },
      { id: 'event-management', name: 'Event Management', clusterId: 'travel-tourism-and-hospitality' },
      { id: 'medical-tourism', name: 'Medical Tourism', clusterId: 'travel-tourism-and-hospitality' },
      { id: 'heritage-and-cultural-tourism', name: 'Heritage & Cultural Tourism', clusterId: 'travel-tourism-and-hospitality' },
      { id: 'hospitality-management', name: 'Hospitality Management', clusterId: 'travel-tourism-and-hospitality' },
    ],
  },
  {
    id: 'creative-art-and-entertainment',
    name: 'Creative, Art & Entertainment',
    industries: [
      { id: 'film-and-media-production', name: 'Film & Media Production', clusterId: 'creative-art-and-entertainment' },
      { id: 'advertising-and-marketing', name: 'Advertising & Marketing', clusterId: 'creative-art-and-entertainment' },
      { id: 'graphic-design', name: 'Graphic Design', clusterId: 'creative-art-and-entertainment' },
      { id: 'music-and-performing-arts', name: 'Music & Performing Arts', clusterId: 'creative-art-and-entertainment' },
      { id: 'gaming-and-animation', name: 'Gaming & Animation', clusterId: 'creative-art-and-entertainment' },
      { id: 'photography', name: 'Photography', clusterId: 'creative-art-and-entertainment' },
      { id: 'content-creation-and-digital-media', name: 'Content Creation & Digital Media', clusterId: 'creative-art-and-entertainment' },
      { id: 'sports-and-fitness', name: 'Sports & Fitness', clusterId: 'creative-art-and-entertainment' },
    ],
  },
  {
    id: 'custom-niche-and-traditional-enterprises',
    name: 'Custom, Niche & Traditional Enterprises',
    industries: [
      { id: 'handicrafts-and-artisanal', name: 'Handicrafts & Artisanal', clusterId: 'custom-niche-and-traditional-enterprises' },
      { id: 'handloom-and-weaving', name: 'Handloom & Weaving', clusterId: 'custom-niche-and-traditional-enterprises' },
      { id: 'jewelry-and-gemstones', name: 'Jewelry & Gemstones', clusterId: 'custom-niche-and-traditional-enterprises' },
      { id: 'ayurveda-and-traditional-medicine', name: 'Ayurveda & Traditional Medicine', clusterId: 'custom-niche-and-traditional-enterprises' },
      { id: 'pottery-and-ceramics', name: 'Pottery & Ceramics', clusterId: 'custom-niche-and-traditional-enterprises' },
      { id: 'leather-goods', name: 'Leather Goods', clusterId: 'custom-niche-and-traditional-enterprises' },
      { id: 'incense-and-essential-oils', name: 'Incense & Essential Oils', clusterId: 'custom-niche-and-traditional-enterprises' },
      { id: 'custom-furniture', name: 'Custom Furniture', clusterId: 'custom-niche-and-traditional-enterprises' },
    ],
  },
  {
    id: 'logistics-and-supply-chain',
    name: 'Logistics & Supply Chain',
    industries: [
      { id: 'freight-and-cargo', name: 'Freight & Cargo', clusterId: 'logistics-and-supply-chain' },
      { id: 'warehousing', name: 'Warehousing', clusterId: 'logistics-and-supply-chain' },
      { id: 'courier-and-delivery', name: 'Courier & Delivery', clusterId: 'logistics-and-supply-chain' },
      { id: 'cold-chain-logistics', name: 'Cold Chain Logistics', clusterId: 'logistics-and-supply-chain' },
      { id: 'shipping-and-maritime', name: 'Shipping & Maritime', clusterId: 'logistics-and-supply-chain' },
      { id: 'fleet-management', name: 'Fleet Management', clusterId: 'logistics-and-supply-chain' },
      { id: 'customs-and-clearing', name: 'Customs & Clearing', clusterId: 'logistics-and-supply-chain' },
      { id: 'last-mile-delivery', name: 'Last Mile Delivery', clusterId: 'logistics-and-supply-chain' },
    ],
  },
];

/**
 * Flat list of all industries across all clusters.
 */
export const ALL_INDUSTRIES: Industry[] = INDUSTRY_CLUSTERS.flatMap(
  (cluster) => cluster.industries
);

/**
 * Lookup map for finding a cluster by its ID.
 */
export const CLUSTER_BY_ID: Record<string, IndustryCluster> = Object.fromEntries(
  INDUSTRY_CLUSTERS.map((cluster) => [cluster.id, cluster])
);

/**
 * Lookup map for finding an industry by its ID.
 */
export const INDUSTRY_BY_ID: Record<string, Industry> = Object.fromEntries(
  ALL_INDUSTRIES.map((industry) => [industry.id, industry])
);
